/**
 * FikoPdfExport - PDF Oluşturma Servisi
 * jsPDF ile şablondan PDF üretimi
 *
 * @author FikoYT
 * @version 1.0.0
 */

import { jsPDF } from 'jspdf';
import {
  PdfTemplate,
  PdfElement,
  PageSettings,
  PAGE_SIZES,
  ModelInfo,
  MeasurementItem,
  AutoDataSource,
  CizimInfo,
  PdfParameter,
  ParameterResolveContext,
  resolveTextWithParameters,
  resolveParameterValue
} from './FikoPdfTemplate';

export interface ExportOptions {
  filename?: string;
  openInNewTab?: boolean;
}

export interface ExportContext {
  modelInfo?: ModelInfo | null;
  measurements?: MeasurementItem[];
  screenshots?: Map<string, string>;  // elementId -> base64 dataUrl
  customData?: Record<string, string>;
  cizimList?: CizimInfo[];  // FikoCizimYalin listesi (seçimli veri kaynağı için)
  // Parametre sistemi için
  parameterValues?: Record<string, any>;  // Parametre override değerleri
  userName?: string;
  projectName?: string;
  companyName?: string;
  revision?: number;
}

export class FikoPdfExport {

  // Font cache - bir kez yükle, her yerde kullan
  private static fontLoaded = false;
  private static fontData: { regular: string; bold: string } | null = null;

  // Varsayılan font adı (Türkçe destekli)
  private readonly FONT_NAME = 'NotoSans';
  private readonly FALLBACK_FONT = 'helvetica';

  /**
   * Türkçe karakter destekli font yükle (Noto Sans)
   * Bu font Türkçe, Arapça, Kiril ve diğer Unicode karakterleri destekler
   *
   * Öncelik sırası:
   * 1. Local assets (/assets/fonts/)
   * 2. CDN (Google Fonts - github raw)
   * 3. Fallback (helvetica)
   */
  private async loadTurkishFont(doc: jsPDF): Promise<boolean> {
    try {
      // Font zaten yüklendiyse tekrar yükleme
      if (FikoPdfExport.fontLoaded && FikoPdfExport.fontData) {
        this.registerFont(doc, FikoPdfExport.fontData);
        return true;
      }

      // 1. Önce local assets'ten dene
      const localBasePath = '/assets/fonts/';
      let regularResponse = await fetch(localBasePath + 'NotoSans-Regular.ttf').catch(() => null);
      let boldResponse = await fetch(localBasePath + 'NotoSans-Bold.ttf').catch(() => null);

      // 2. Local yoksa Google Fonts'tan yükle (TTF - Türkçe karakterleri destekler)
      if (!regularResponse?.ok) {
        console.log('[FikoPdfExport] Local font bulunamadı, Google Fonts\'tan yükleniyor...');

        // Google Fonts resmi TTF URL'leri (Roboto - Latin Extended dahil, Türkçe ğüşıöç destekli)
        const googleFonts = {
          regular: 'https://fonts.gstatic.com/s/roboto/v50/KFOMCnqEu92Fr1ME7kSn66aGLdTylUAMQXC89YmC2DPNWubEbWmT.ttf',
          bold: 'https://fonts.gstatic.com/s/roboto/v50/KFOMCnqEu92Fr1ME7kSn66aGLdTylUAMQXC89YmC2DPNWuYjammT.ttf'
        };

        [regularResponse, boldResponse] = await Promise.all([
          fetch(googleFonts.regular).catch(() => null),
          fetch(googleFonts.bold).catch(() => null)
        ]);

        if (regularResponse?.ok) {
          console.log('[FikoPdfExport] Google Fonts\'tan Roboto TTF yüklendi.');
        }
      }

      // 3. CDN de başarısızsa fallback kullan
      if (!regularResponse?.ok) {
        console.warn('[FikoPdfExport] Türkçe font yüklenemedi, varsayılan font kullanılacak.');
        console.warn('[FikoPdfExport] Türkçe karakterler düzgün görünmeyebilir.');
        return false;
      }

      // Font verilerini base64'e çevir
      const regularBuffer = await regularResponse.arrayBuffer();
      const regularBase64 = this.arrayBufferToBase64(regularBuffer);

      let boldBase64 = regularBase64; // Bold yoksa regular kullan
      if (boldResponse?.ok) {
        const boldBuffer = await boldResponse.arrayBuffer();
        boldBase64 = this.arrayBufferToBase64(boldBuffer);
      }

      // Cache'e kaydet
      FikoPdfExport.fontData = { regular: regularBase64, bold: boldBase64 };
      FikoPdfExport.fontLoaded = true;

      // Font'u jsPDF'e kaydet
      this.registerFont(doc, FikoPdfExport.fontData);

      console.log('[FikoPdfExport] Türkçe font başarıyla yüklendi.');
      return true;
    } catch (error) {
      console.error('[FikoPdfExport] Font yüklenirken hata:', error);
      return false;
    }
  }

  /**
   * Font'u jsPDF'e kaydet
   */
  private registerFont(doc: jsPDF, fontData: { regular: string; bold: string }): void {
    // Regular font
    doc.addFileToVFS('NotoSans-Regular.ttf', fontData.regular);
    doc.addFont('NotoSans-Regular.ttf', this.FONT_NAME, 'normal');

    // Bold font
    doc.addFileToVFS('NotoSans-Bold.ttf', fontData.bold);
    doc.addFont('NotoSans-Bold.ttf', this.FONT_NAME, 'bold');
  }

  /**
   * ArrayBuffer'ı base64 string'e çevir
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Aktif font adını döndür (Türkçe font yüklendiyse onu, yoksa fallback)
   */
  private getActiveFont(): string {
    return FikoPdfExport.fontLoaded ? this.FONT_NAME : this.FALLBACK_FONT;
  }

  /**
   * Şablondan PDF oluştur
   */
  async generate(template: PdfTemplate, context: ExportContext = {}): Promise<jsPDF> {
    const { pageSettings } = template;

    // Parametre çözümleme bağlamını oluştur
    const paramContext = this.buildParameterContext(template, context);

    // Parametreleri çözümle ve override değerlerini uygula
    const resolvedParameters = this.resolveAllParameters(template.parameters || [], paramContext, context.parameterValues);

    // Sayfa boyutlarını ayarla
    const orientation = pageSettings.orientation === 'landscape' ? 'l' : 'p';
    const format = this.getPageFormat(pageSettings);

    const doc = new jsPDF({
      orientation,
      unit: 'mm',
      format
    });

    // Türkçe karakter destekli font yükle
    await this.loadTurkishFont(doc);

    // Varsayılan font'u ayarla
    doc.setFont(this.getActiveFont(), 'normal');

    // Arka plan rengi
    if (pageSettings.backgroundColor && pageSettings.backgroundColor !== '#ffffff') {
      doc.setFillColor(pageSettings.backgroundColor);
      doc.rect(0, 0, pageSettings.width, pageSettings.height, 'F');
    }

    // Elementleri z-index sırasına göre çiz
    const sortedElements = [...template.elements]
      .filter(el => el.visible)
      .sort((a, b) => a.zIndex - b.zIndex);

    for (const element of sortedElements) {
      await this.renderElement(doc, element, pageSettings, context, resolvedParameters, paramContext);
    }

    return doc;
  }

  /**
   * Parametre çözümleme bağlamını oluştur
   */
  private buildParameterContext(template: PdfTemplate, context: ExportContext): ParameterResolveContext {
    return {
      cizimler: context.cizimList,
      modelInfo: context.modelInfo || undefined,
      parameters: template.parameters,
      userName: context.userName || context.customData?.userName,
      projectName: context.projectName || context.customData?.projectName,
      companyName: context.companyName || context.customData?.companyName,
      pageNumber: 1,
      totalPages: 1,
      revision: context.revision || 1
    };
  }

  /**
   * Tüm parametreleri çözümle
   */
  private resolveAllParameters(
    parameters: PdfParameter[],
    paramContext: ParameterResolveContext,
    overrideValues?: Record<string, any>
  ): Map<string, string> {
    const resolved = new Map<string, string>();

    for (const param of parameters) {
      // Override değeri varsa önce onu kullan
      if (overrideValues && param.name in overrideValues) {
        const override = overrideValues[param.name];
        resolved.set(param.name, String(override));
      } else {
        // Parametreyi çözümle
        const value = resolveParameterValue(param, paramContext);
        resolved.set(param.name, value);
      }
    }

    return resolved;
  }

  /**
   * Metin içindeki parametre referanslarını çözümle
   */
  private resolveText(
    text: string,
    resolvedParameters: Map<string, string>
  ): string {
    if (!text) return text;

    const paramPattern = /\{\{(\w+)\}\}/g;

    return text.replace(paramPattern, (match, paramName) => {
      const value = resolvedParameters.get(paramName);
      return value !== undefined ? value : match;
    });
  }

  /**
   * PDF'i indir
   */
  async download(template: PdfTemplate, context: ExportContext = {}, options: ExportOptions = {}): Promise<void> {
    const doc = await this.generate(template, context);
    const filename = options.filename || `${template.name || 'rapor'}.pdf`;
    doc.save(filename);
  }

  /**
   * PDF'i yeni sekmede aç
   */
  async openInNewTab(template: PdfTemplate, context: ExportContext = {}): Promise<void> {
    const doc = await this.generate(template, context);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }

  /**
   * PDF'i blob olarak döndür
   */
  async toBlob(template: PdfTemplate, context: ExportContext = {}): Promise<Blob> {
    const doc = await this.generate(template, context);
    return doc.output('blob');
  }

  /**
   * PDF'i base64 olarak döndür
   */
  async toBase64(template: PdfTemplate, context: ExportContext = {}): Promise<string> {
    const doc = await this.generate(template, context);
    return doc.output('datauristring');
  }

  // ============ ELEMENT RENDER ============

  private async renderElement(
    doc: jsPDF,
    element: PdfElement,
    pageSettings: PageSettings,
    context: ExportContext,
    resolvedParameters: Map<string, string>,
    paramContext: ParameterResolveContext
  ): Promise<void> {
    // WYSIWYG: Element pozisyonları doğrudan kullanılır (margin offset yok)
    // Margin'ler sadece editörde görsel kılavuz olarak gösterilir
    const x = element.x;
    const y = element.y;

    // Rotation desteği (jsPDF'de sınırlı)
    // Not: Karmaşık rotasyonlar için canvas pre-render gerekebilir

    switch (element.type) {
      case 'text':
        await this.renderText(doc, element, x, y, resolvedParameters);
        break;

      case 'image':
      case 'screenshot':
        await this.renderImage(doc, element, x, y, context);
        break;

      case 'table':
        this.renderTable(doc, element, x, y, resolvedParameters);
        break;

      case 'auto-data':
        await this.renderAutoData(doc, element, x, y, context, resolvedParameters);
        break;

      case 'shape':
        await this.renderShapeAsImage(doc, element, x, y);
        break;

      case 'measurement':
        this.renderMeasurements(doc, element, x, y, context);
        break;

      case 'qrcode':
        await this.renderQRCode(doc, element, x, y, resolvedParameters);
        break;
    }
  }

  // ============ TEXT ============

  private async renderText(
    doc: jsPDF,
    element: PdfElement,
    x: number,
    y: number,
    resolvedParameters: Map<string, string>
  ): Promise<void> {
    const props = element.properties;
    let content = props.content || '';

    if (!content) return;

    // Parametre referanslarını çözümle ({{param_name}} formatı)
    content = this.resolveText(content, resolvedParameters);

    // Font ayarları
    const fontSize = props.fontSize || 12;
    const fontColor = props.fontColor || '#000000';
    const fontWeight = props.fontWeight || 'normal';
    const textAlign = props.textAlign || 'left';

    doc.setFontSize(fontSize);
    doc.setTextColor(fontColor);

    // Font weight (jsPDF'de sınırlı)
    if (fontWeight === 'bold') {
      doc.setFont(this.getActiveFont(), 'bold');
    } else {
      doc.setFont(this.getActiveFont(), 'normal');
    }

    // Arka plan
    if (props.backgroundColor) {
      doc.setFillColor(props.backgroundColor);
      doc.rect(x, y, element.width, element.height, 'F');
    }

    // Metin hizalama
    let textX = x;
    if (textAlign === 'center') {
      textX = x + element.width / 2;
    } else if (textAlign === 'right') {
      textX = x + element.width;
    }

    // Emoji kontrolü - emoji varsa özel render kullan
    if (this.hasEmoji(content)) {
      await this.renderTextWithEmojis(doc, content, textX, y, element.width, fontSize, fontColor, textAlign);
      return;
    }

    // Normal metin çiz (çok satırlı destek)
    const lines = doc.splitTextToSize(content, element.width);
    const lineHeight = fontSize * 0.4; // mm cinsinden yaklaşık

    doc.text(lines, textX, y + fontSize * 0.35, {
      align: textAlign as any,
      maxWidth: element.width
    });
  }

  /**
   * Metinde emoji var mı kontrol et
   */
  private hasEmoji(text: string): boolean {
    // Regex'i yeni oluştur (g flag state sorunu için)
    const emojiRegex = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})/gu;
    return emojiRegex.test(text);
  }

  /**
   * Emoji içeren metni render et
   * Emojiler canvas üzerinden resim olarak eklenir
   */
  private async renderTextWithEmojis(
    doc: jsPDF,
    content: string,
    x: number,
    y: number,
    maxWidth: number,
    fontSize: number,
    fontColor: string,
    textAlign: string
  ): Promise<void> {
    // Metni emoji ve text parçalarına ayır
    const segments = this.splitTextAndEmojis(content);

    // Her satır için
    const lineHeight = fontSize * 0.5; // mm
    let currentX = x;
    let currentY = y + fontSize * 0.35;

    // Alignment için offset hesapla
    if (textAlign === 'center' || textAlign === 'right') {
      const totalWidth = this.calculateSegmentsWidth(doc, segments, fontSize);
      if (textAlign === 'center') {
        currentX = x - totalWidth / 2;
      } else {
        currentX = x - totalWidth;
      }
    }

    for (const segment of segments) {
      if (segment.type === 'emoji') {
        // Emoji'yi resim olarak ekle
        try {
          const emojiSize = fontSize * 0.4; // mm cinsinden
          const dataUrl = await this.emojiToDataUrl(segment.content, fontSize * 4);
          doc.addImage(dataUrl, 'PNG', currentX, currentY - emojiSize * 0.8, emojiSize, emojiSize);
          currentX += emojiSize + 0.5; // emoji genişliği + boşluk
        } catch (e) {
          // Emoji render başarısızsa placeholder koy
          doc.text('□', currentX, currentY);
          currentX += fontSize * 0.3;
        }
      } else {
        // Normal text
        doc.text(segment.content, currentX, currentY);
        currentX += doc.getTextWidth(segment.content);
      }
    }
  }

  /**
   * Metni text ve emoji segmentlerine ayır
   */
  private splitTextAndEmojis(text: string): Array<{ type: 'text' | 'emoji'; content: string }> {
    const segments: Array<{ type: 'text' | 'emoji'; content: string }> = [];
    const regex = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})/gu;

    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Emoji öncesi text
      if (match.index > lastIndex) {
        segments.push({
          type: 'text',
          content: text.slice(lastIndex, match.index)
        });
      }

      // Emoji
      segments.push({
        type: 'emoji',
        content: match[0]
      });

      lastIndex = match.index + match[0].length;
    }

    // Kalan text
    if (lastIndex < text.length) {
      segments.push({
        type: 'text',
        content: text.slice(lastIndex)
      });
    }

    return segments;
  }

  /**
   * Segment'lerin toplam genişliğini hesapla
   */
  private calculateSegmentsWidth(
    doc: jsPDF,
    segments: Array<{ type: 'text' | 'emoji'; content: string }>,
    fontSize: number
  ): number {
    let totalWidth = 0;
    const emojiWidth = fontSize * 0.4;

    for (const segment of segments) {
      if (segment.type === 'emoji') {
        totalWidth += emojiWidth + 0.5;
      } else {
        totalWidth += doc.getTextWidth(segment.content);
      }
    }

    return totalWidth;
  }

  /**
   * Emoji'yi canvas üzerinden PNG data URL'e çevir
   */
  private emojiToDataUrl(emoji: string, size: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Canvas context oluşturulamadı'));
        return;
      }

      // Emoji'yi canvas'a çiz
      ctx.font = `${size * 0.8}px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(emoji, size / 2, size / 2);

      resolve(canvas.toDataURL('image/png'));
    });
  }

  // ============ IMAGE / SCREENSHOT ============

  private async renderImage(
    doc: jsPDF,
    element: PdfElement,
    x: number,
    y: number,
    context: ExportContext
  ): Promise<void> {
    let imageData: string | undefined;

    if (element.type === 'screenshot') {
      // Screenshot context'ten al
      imageData = context.screenshots?.get(element.id);
    } else {
      // Normal image src'den al
      imageData = element.properties.src;
    }

    if (!imageData) {
      // Placeholder çiz
      doc.setDrawColor('#cccccc');
      doc.setFillColor('#f0f0f0');
      doc.rect(x, y, element.width, element.height, 'FD');
      doc.setFontSize(8);
      doc.setTextColor('#999999');
      doc.text('Görsel yok', x + element.width / 2, y + element.height / 2, { align: 'center' });
      return;
    }

    try {
      // Format tespiti
      const format = imageData.includes('image/png') ? 'PNG' : 'JPEG';

      // Resmin oranını koruyarak boyutları hesapla
      const imgDimensions = await this.getImageDimensions(imageData);
      const { renderWidth, renderHeight, offsetX, offsetY } = this.calculateFitDimensions(
        imgDimensions.width,
        imgDimensions.height,
        element.width,
        element.height
      );

      // Border radius varsa canvas ile yuvarlatılmış resim oluştur
      const borderRadius = element.properties.borderRadius || 0;
      if (borderRadius > 0) {
        // Canvas ile yuvarlatılmış resim oluştur
        const roundedImage = await this.createRoundedImage(imageData, renderWidth, renderHeight, borderRadius);
        doc.addImage(roundedImage, 'PNG', x + offsetX, y + offsetY, renderWidth, renderHeight);
      } else {
        // Normal resim ekle
        doc.addImage(imageData, format, x + offsetX, y + offsetY, renderWidth, renderHeight);
      }

      // Border (yeni özellikler: borderWidth, borderStyle, borderColor)
      const borderWidth = element.properties.borderWidth || 0;
      const borderStyle = element.properties.borderStyle || 'none';
      const borderColor = element.properties.borderColor || '#000000';

      if (borderWidth > 0 && borderStyle !== 'none') {
        doc.setDrawColor(borderColor);
        doc.setLineWidth(borderWidth * 0.35); // px to mm yaklaşık dönüşüm

        // Kesikli çizgi için
        if (borderStyle === 'dashed') {
          // jsPDF'de kesikli çizgi desteği sınırlı, düz çizgi kullan
        } else if (borderStyle === 'dotted') {
          // Noktalı çizgi desteği sınırlı
        }

        // Köşe yuvarlaklığı varsa yuvarlatılmış dikdörtgen çiz
        if (borderRadius > 0) {
          this.drawRoundedRect(doc, x, y, element.width, element.height, borderRadius * 0.35);
        } else {
          doc.rect(x, y, element.width, element.height, 'S');
        }
      }

      // Eski format border desteği (geriye uyumluluk)
      if (element.properties.border && !borderWidth) {
        const border = element.properties.border;
        doc.setDrawColor(border.color || '#000000');
        doc.setLineWidth(border.width || 0.5);
        doc.rect(x, y, element.width, element.height, 'S');
      }
    } catch (error) {
      console.error('[FikoPdfExport] Image render hatası:', error);
    }
  }

  // Resmin doğal boyutlarını al
  private getImageDimensions(imageData: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        // Hata durumunda varsayılan 1:1 oran
        resolve({ width: 100, height: 100 });
      };
      img.src = imageData;
    });
  }

  // Resmi element içine sığdırmak için boyutları hesapla (object-fit: contain mantığı)
  private calculateFitDimensions(
    imageWidth: number,
    imageHeight: number,
    containerWidth: number,
    containerHeight: number
  ): { renderWidth: number; renderHeight: number; offsetX: number; offsetY: number } {
    const imageAspect = imageWidth / imageHeight;
    const containerAspect = containerWidth / containerHeight;

    let renderWidth: number;
    let renderHeight: number;
    let offsetX: number;
    let offsetY: number;

    if (imageAspect > containerAspect) {
      // Resim daha geniş - genişliğe sığdır
      renderWidth = containerWidth;
      renderHeight = containerWidth / imageAspect;
      offsetX = 0;
      offsetY = (containerHeight - renderHeight) / 2;
    } else {
      // Resim daha uzun - yüksekliğe sığdır
      renderHeight = containerHeight;
      renderWidth = containerHeight * imageAspect;
      offsetX = (containerWidth - renderWidth) / 2;
      offsetY = 0;
    }

    return { renderWidth, renderHeight, offsetX, offsetY };
  }

  // Canvas ile yuvarlatılmış köşeli resim oluştur
  private createRoundedImage(
    imageData: string,
    width: number,
    height: number,
    radius: number
  ): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        // Yüksek çözünürlük için scale faktörü
        const scale = 3;
        const canvas = document.createElement('canvas');
        canvas.width = width * scale;
        canvas.height = height * scale;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          resolve(imageData); // Fallback
          return;
        }

        // Yuvarlatılmış dikdörtgen kırpma yolu oluştur
        const r = radius * scale;
        const w = canvas.width;
        const h = canvas.height;

        ctx.beginPath();
        ctx.moveTo(r, 0);
        ctx.lineTo(w - r, 0);
        ctx.quadraticCurveTo(w, 0, w, r);
        ctx.lineTo(w, h - r);
        ctx.quadraticCurveTo(w, h, w - r, h);
        ctx.lineTo(r, h);
        ctx.quadraticCurveTo(0, h, 0, h - r);
        ctx.lineTo(0, r);
        ctx.quadraticCurveTo(0, 0, r, 0);
        ctx.closePath();
        ctx.clip();

        // Resmi çiz
        ctx.drawImage(img, 0, 0, w, h);

        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(imageData);
      img.src = imageData;
    });
  }

  // jsPDF ile yuvarlatılmış dikdörtgen çiz
  private drawRoundedRect(
    doc: jsPDF,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ): void {
    const r = Math.min(radius, width / 2, height / 2);
    // jsPDF'in yerleşik roundedRect metodunu kullan
    (doc as any).roundedRect(x, y, width, height, r, r, 'S');
  }

  // ============ TABLE ============

  private renderTable(
    doc: jsPDF,
    element: PdfElement,
    x: number,
    y: number,
    resolvedParameters: Map<string, string>
  ): void {
    const props = element.properties;
    const rows = props.rows || 3;
    const cols = props.cols || 3;
    const data = props.data || [];
    const headerRow = props.headerRow ?? true;
    const borderColor = props.borderColor || '#000000';
    const headerBgColor = props.headerBgColor || '#e0e0e0';
    const cellPadding = props.cellPadding || 2;

    const cellWidth = element.width / cols;
    const cellHeight = element.height / rows;

    doc.setDrawColor(borderColor);
    doc.setLineWidth(0.3);
    doc.setFontSize(9);

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const cellX = x + col * cellWidth;
        const cellY = y + row * cellHeight;

        // Header arka planı
        if (row === 0 && headerRow) {
          doc.setFillColor(headerBgColor);
          doc.rect(cellX, cellY, cellWidth, cellHeight, 'FD');
          doc.setFont(this.getActiveFont(), 'bold');
        } else {
          doc.rect(cellX, cellY, cellWidth, cellHeight, 'S');
          doc.setFont(this.getActiveFont(), 'normal');
        }

        // Hücre içeriği - parametre referanslarını çözümle
        let cellData = data[row]?.[col] || '';
        if (cellData) {
          cellData = this.resolveText(cellData, resolvedParameters);
          doc.setTextColor('#000000');
          doc.text(
            cellData,
            cellX + cellPadding,
            cellY + cellHeight / 2 + 1,
            { maxWidth: cellWidth - cellPadding * 2 }
          );
        }
      }
    }
  }

  // ============ AUTO-DATA ============

  private async renderAutoData(
    doc: jsPDF,
    element: PdfElement,
    x: number,
    y: number,
    context: ExportContext,
    resolvedParameters: Map<string, string>
  ): Promise<void> {
    const props = element.properties;
    const dataSource = props.dataSource || 'date';
    let prefix = props.prefix || '';
    let suffix = props.suffix || '';
    const fontSize = props.fontSize || 10;
    const sourceObjectId = props.sourceObjectId;

    // Prefix ve suffix'te parametre referanslarını çözümle
    prefix = this.resolveText(prefix, resolvedParameters);
    suffix = this.resolveText(suffix, resolvedParameters);

    const dimensionSort = props.dimensionSort ?? false;
    let value = this.getAutoDataValue(dataSource, context, sourceObjectId, dimensionSort);
    const content = `${prefix}${value}${suffix}`;

    // Text olarak çiz
    doc.setFontSize(fontSize);
    doc.setTextColor(props.fontColor || '#000000');
    doc.setFont(this.getActiveFont(), 'normal');

    // Emoji kontrolü
    if (this.hasEmoji(content)) {
      await this.renderTextWithEmojis(doc, content, x, y, element.width, fontSize, props.fontColor || '#000000', 'left');
      return;
    }

    doc.text(content, x, y + fontSize * 0.35);
  }

  // Kaynak veriyi belirle (seçili nesne veya genel model)
  private getSourceData(context: ExportContext, sourceObjectId?: string): ModelInfo | CizimInfo | null {
    if (sourceObjectId && context.cizimList) {
      const cizim = context.cizimList.find(c => c.id === sourceObjectId);
      if (cizim) return cizim as any;
    }
    return context.modelInfo || null;
  }

  private getAutoDataValue(source: AutoDataSource, context: ExportContext, sourceObjectId?: string, dimensionSort?: boolean): string {
    const sourceData = this.getSourceData(context, sourceObjectId);
    const info = context.modelInfo;

    switch (source) {
      case 'date':
        return new Date().toLocaleDateString('tr-TR');

      case 'datetime':
        return new Date().toLocaleString('tr-TR');

      case 'filename':
        return (sourceData as any)?.filename || (sourceData as any)?.name || info?.filename || 'Model';

      case 'dimensions':
        const dims = sourceData?.dimensions || info?.dimensions;
        if (!dims) return '-';

        // Boyutları sırala (büyük → orta → küçük)
        if (dimensionSort) {
          const sorted = [dims.x, dims.y, dims.z].sort((a, b) => b - a);
          return `${sorted[0].toFixed(1)} × ${sorted[1].toFixed(1)} × ${sorted[2].toFixed(1)} mm`;
        }
        return `${dims.x.toFixed(1)} × ${dims.y.toFixed(1)} × ${dims.z.toFixed(1)} mm`;

      case 'volume':
        const vol = sourceData?.volume ?? info?.volume;
        return vol ? `${vol.toFixed(2)} cm³` : '-';

      case 'surface-area':
        const area = sourceData?.surfaceArea ?? info?.surfaceArea;
        return area ? `${area.toFixed(2)} cm²` : '-';

      case 'weight':
        const weight = sourceData?.weight ?? info?.weight;
        return weight ? this.formatWeight(weight) : '-';

      case 'material':
        return sourceData?.material || info?.material || '-';

      case 'center-of-mass':
        if (!info?.centerOfMass) return '-';
        const c = info.centerOfMass;
        return `(${c.x.toFixed(1)}, ${c.y.toFixed(1)}, ${c.z.toFixed(1)})`;

      case 'measurement-count':
        return `${context.measurements?.length || 0} ölçüm`;

      case 'project-name':
        return context.customData?.projectName || 'Proje';

      case 'user-name':
        return context.customData?.userName || 'Kullanıcı';

      default:
        return '-';
    }
  }

  /**
   * Ağırlığı uygun birime çevirir (kg → g → mg)
   * @param weightKg Ağırlık (kg cinsinden)
   */
  private formatWeight(weightKg: number): string {
    if (!weightKg || isNaN(weightKg)) {
      return '0 kg';
    }

    const weightGram = weightKg * 1000;
    const weightMilligram = weightGram * 1000;

    // En uygun birimi seç
    if (weightKg >= 1) {
      return `${weightKg.toFixed(2)} kg`;
    } else if (weightGram >= 1) {
      return `${weightGram.toFixed(2)} g`;
    } else {
      return `${weightMilligram.toFixed(2)} mg`;
    }
  }

  // ============ SHAPE (IMAGE-BASED) ============

  /**
   * Şekli SVG olarak oluştur, canvas'a çiz ve PDF'e resim olarak ekle
   * Bu yöntem preview ile birebir aynı görünümü sağlar
   *
   * Not: Tarayıcılar arası tutarlılık için "iki aşamalı render" kullanıyoruz.
   * Chrome ve Firefox, preserveAspectRatio="none" ile SVG'yi canvas'a farklı çiziyor.
   * Çözüm: SVG'yi önce viewBox boyutunda (100x100) çiz, sonra istenen boyuta ölçekle.
   * Kaynak: https://github.com/whatwg/html/issues/6546
   */
  private async renderShapeAsImage(doc: jsPDF, element: PdfElement, x: number, y: number): Promise<void> {
    const props = element.properties;
    const shapeType = props.shapeType || 'rect';
    const strokeColor = props.strokeColor || '#000000';
    const strokeWidth = props.strokeWidth || 1;
    const fillColor = props.fillColor || 'none';
    const lineStyle = props.lineStyle || 'solid';

    // PDF boyutları (mm cinsinden)
    const pdfWidth = element.width;
    const pdfHeight = element.height;

    // Final canvas boyutu (yüksek çözünürlük için)
    const pixelsPerMm = 8;
    const finalWidth = Math.round(pdfWidth * pixelsPerMm);
    const finalHeight = Math.round(pdfHeight * pixelsPerMm);

    // Çizgi stili
    let strokeDasharray = '';
    if (lineStyle === 'dashed') {
      strokeDasharray = 'stroke-dasharray="5,3"';
    } else if (lineStyle === 'dotted') {
      strokeDasharray = 'stroke-dasharray="2,2"';
    }

    // Fill değerini kontrol et
    const safeFillColor = fillColor === 'none' ? 'transparent' : fillColor;

    // SVG içeriğini oluştur
    const svgContent = this.getShapeSvgContent(shapeType, strokeColor, strokeWidth, fillColor, props);

    // SVG: viewBox ve width/height aynı (100x100) - tarayıcı tutarlılığı için
    // preserveAspectRatio yok çünkü 1:1 ölçekleme yapıyoruz
    const svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100" stroke="${strokeColor}" stroke-width="${strokeWidth}" fill="${safeFillColor}" stroke-linejoin="miter" stroke-linecap="butt" ${strokeDasharray}>${svgContent}</svg>`;

    try {
      // İki aşamalı render: SVG -> 100x100 bitmap -> final boyut
      const dataUrl = await this.svgToDataUrlScaled(svgString, finalWidth, finalHeight);

      // PDF'e resim olarak ekle
      doc.addImage(dataUrl, 'PNG', x, y, pdfWidth, pdfHeight);
    } catch (error) {
      console.error('[FikoPdfExport] Shape render HATASI:', error);
      // Fallback: Eski yöntemi kullan
      this.renderShape(doc, element, x, y);
    }
  }

  /**
   * SVG'yi iki aşamalı render ile PNG'ye çevir (tarayıcı tutarlılığı için)
   *
   * Aşama 1: SVG'yi doğal boyutunda (100x100) bir canvas'a çiz
   * Aşama 2: Bu bitmap'i istenen boyuta ölçekleyerek final canvas'a çiz
   *
   * Bu yöntem Chrome/Firefox/Safari arasındaki preserveAspectRatio tutarsızlığını çözer.
   * Kaynak: https://github.com/whatwg/html/issues/6546
   */
  private svgToDataUrlScaled(svgString: string, finalWidth: number, finalHeight: number): Promise<string> {
    return new Promise((resolve, reject) => {
      // SVG'nin doğal boyutu (viewBox ile aynı)
      const svgNaturalSize = 100;

      const img = new Image();

      // Base64 encoding kullan
      const encoder = new TextEncoder();
      const uint8Array = encoder.encode(svgString);
      let binaryString = '';
      uint8Array.forEach(byte => binaryString += String.fromCharCode(byte));
      const svgBase64 = btoa(binaryString);
      const dataUri = `data:image/svg+xml;base64,${svgBase64}`;

      img.onload = () => {
        // Aşama 1: SVG'yi doğal boyutunda (100x100) temp canvas'a çiz
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = svgNaturalSize;
        tempCanvas.height = svgNaturalSize;
        const tempCtx = tempCanvas.getContext('2d');

        if (!tempCtx) {
          reject(new Error('Temp canvas context oluşturulamadı'));
          return;
        }

        // SVG'yi 1:1 oranında çiz (preserveAspectRatio sorunu yok)
        tempCtx.drawImage(img, 0, 0, svgNaturalSize, svgNaturalSize);

        // Aşama 2: Temp canvas'ı final boyuta ölçekle
        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = finalWidth;
        finalCanvas.height = finalHeight;
        const finalCtx = finalCanvas.getContext('2d');

        if (!finalCtx) {
          reject(new Error('Final canvas context oluşturulamadı'));
          return;
        }

        // Kaliteli ölçekleme için
        finalCtx.imageSmoothingEnabled = true;
        finalCtx.imageSmoothingQuality = 'high';

        // Temp canvas'ı final boyuta ölçekleyerek çiz
        finalCtx.drawImage(tempCanvas, 0, 0, svgNaturalSize, svgNaturalSize, 0, 0, finalWidth, finalHeight);

        resolve(finalCanvas.toDataURL('image/png'));
      };

      img.onerror = (err) => {
        console.error('[SVG->Canvas] Image yükleme HATASI:', err);
        reject(err);
      };

      img.src = dataUri;
    });
  }

  /**
   * Şekil tipine göre SVG içeriği döndür (Angular template ile birebir aynı)
   */
  private getShapeSvgContent(
    shapeType: string,
    strokeColor: string,
    strokeWidth: number,
    fillColor: string,
    props: any
  ): string {
    switch (shapeType) {
      case 'line':
        return '<line x1="0" y1="50" x2="100" y2="50" />';

      case 'rect':
        return '<rect x="0" y="0" width="100" height="100" />';

      case 'rounded-rect':
        return '<rect x="0" y="0" width="100" height="100" rx="5" ry="5" />';

      case 'circle':
        return '<circle cx="50" cy="50" r="50" />';

      case 'ellipse':
        return '<ellipse cx="50" cy="50" rx="50" ry="50" />';

      case 'semicircle':
        return '<path d="M 0 100 A 50 50 0 0 1 100 100 L 50 100 Z" />';

      case 'quarter-circle':
        return '<path d="M 0 100 A 100 100 0 0 1 100 0 L 100 100 Z" />';

      case 'triangle':
        return '<polygon points="50,0 100,100 0,100" />';

      case 'right-triangle':
        return '<polygon points="0,0 100,100 0,100" />';

      case 'diamond':
        return '<polygon points="50,0 100,50 50,100 0,50" />';

      case 'parallelogram':
        return '<polygon points="20,0 100,0 80,100 0,100" />';

      case 'trapezoid':
        return '<polygon points="15,0 85,0 100,100 0,100" />';

      case 'pentagon':
        return `<polygon points="${this.getRegularPolygonPointsString(5)}" />`;

      case 'hexagon':
        return `<polygon points="${this.getRegularPolygonPointsString(6)}" />`;

      case 'octagon':
        return `<polygon points="${this.getRegularPolygonPointsString(8)}" />`;

      case 'arrow':
        return `<g>
          <line x1="2" y1="50" x2="88" y2="50" />
          <polyline points="82,38 98,50 82,62" fill="none" />
        </g>`;

      case 'double-arrow':
        return `<g>
          <line x1="12" y1="50" x2="88" y2="50" />
          <polyline points="18,38 2,50 18,62" fill="none" />
          <polyline points="82,38 98,50 82,62" fill="none" />
        </g>`;

      case 'curved-arrow':
        return `<g>
          <path d="M 2 70 C 30 2, 70 2, 98 30" fill="none" />
          <polyline points="88,22 98,30 90,40" fill="none" />
        </g>`;

      case 'chevron':
        return '<polygon points="0,0 80,0 100,50 80,100 0,100 20,50" />';

      case 'star':
        const starPoints = props.starPoints || 5;
        const innerRadius = props.starInnerRadius || 0.4;
        return `<polygon points="${this.getStarPointsString(starPoints, innerRadius)}" />`;

      case 'heart':
        const heartFill = fillColor !== 'none' ? fillColor : '#e74c3c';
        return `<path d="M 50 95 C 10 60, 2 35, 2 25 C 2 10, 18 2, 30 2 C 40 2, 48 10, 50 18 C 52 10, 60 2, 70 2 C 82 2, 98 10, 98 25 C 98 35, 90 60, 50 95 Z" fill="${heartFill}" />`;

      case 'cross':
        return `<g>
          <rect x="0" y="33.33" width="100" height="33.33" />
          <rect x="33.33" y="0" width="33.33" height="100" />
        </g>`;

      case 'x-mark':
        return `<g>
          <line x1="0" y1="0" x2="100" y2="100" stroke-width="8" />
          <line x1="100" y1="0" x2="0" y2="100" stroke-width="8" />
        </g>`;

      case 'checkmark':
        const checkColor = strokeColor || '#27ae60';
        return `<polyline points="15,50 40,75 85,20" fill="none" stroke="${checkColor}" stroke-width="8" stroke-linecap="round" stroke-linejoin="round" />`;

      case 'lightning':
        const lightningFill = fillColor !== 'none' ? fillColor : '#f39c12';
        return `<polygon points="55,0 25,45 50,45 35,100 75,50 50,50 70,0" fill="${lightningFill}" />`;

      case 'cloud':
        const cloudFill = fillColor !== 'none' ? fillColor : '#ecf0f1';
        const cloudStroke = strokeColor || '#3498db';
        return `<g fill="${cloudFill}" stroke="${cloudStroke}">
          <circle cx="25" cy="60" r="20" />
          <circle cx="45" cy="40" r="25" />
          <circle cx="70" cy="50" r="22" />
          <circle cx="50" cy="65" r="20" />
        </g>`;

      case 'speech-bubble':
        const bubbleFill = fillColor !== 'none' ? fillColor : '#fff';
        return `<g fill="${bubbleFill}">
          <rect x="0" y="0" width="100" height="75" rx="11" ry="11" />
          <polygon points="20,74 10,100 35,74" />
        </g>`;

      case 'gear':
        const gearFill = fillColor !== 'none' ? fillColor : '#95a5a6';
        const gearStroke = strokeColor || '#7f8c8d';
        return `<g fill="${gearFill}" stroke="${gearStroke}">
          <polygon points="${this.getGearPointsString(50, 50, 45)}" />
          <circle cx="50" cy="50" r="12" fill="#fff" stroke="${gearStroke}" />
        </g>`;

      case 'bracket-left':
        return '<path d="M 70 5 L 30 5 L 30 95 L 70 95" fill="none" stroke-width="4" />';

      case 'bracket-right':
        return '<path d="M 30 5 L 70 5 L 70 95 L 30 95" fill="none" stroke-width="4" />';

      case 'brace-left':
        return '<path d="M 70 5 L 55 10 L 50 25 L 50 45 L 30 50 L 50 55 L 50 75 L 55 90 L 70 95" fill="none" stroke-width="3" />';

      case 'brace-right':
        return '<path d="M 30 5 L 45 10 L 50 25 L 50 45 L 70 50 L 50 55 L 50 75 L 45 90 L 30 95" fill="none" stroke-width="3" />';

      case 'angle-bracket-l':
        return '<polyline points="75,5 25,50 75,95" fill="none" stroke-width="5" />';

      case 'angle-bracket-r':
        return '<polyline points="25,5 75,50 25,95" fill="none" stroke-width="5" />';

      // ═══════════════ YENİ OKLAR ═══════════════
      case 'block-arrow':
        const blockFill = fillColor !== 'none' ? fillColor : '#3498db';
        return `<polygon points="0,30 60,30 60,10 100,50 60,90 60,70 0,70" fill="${blockFill}" />`;

      case 'bent-arrow':
        const bentFill = fillColor !== 'none' ? fillColor : '#3498db';
        return `<path d="M 10 90 L 10 40 L 50 40 L 50 20 L 90 50 L 50 80 L 50 60 L 30 60 L 30 90 Z" fill="${bentFill}" />`;

      case 'circular-arrow':
        return `<g fill="none" stroke-width="6" stroke-linecap="round">
          <path d="M 75 15 A 35 35 0 1 1 15 50" />
          <polygon points="15,35 5,55 25,55" fill="${strokeColor}" stroke="none" />
        </g>`;

      // ═══════════════ ENDÜSTRİYEL SİMGELER ═══════════════
      case 'gear-double':
        const gearDblFill = fillColor !== 'none' ? fillColor : '#95a5a6';
        const gearDblStroke = strokeColor || '#7f8c8d';
        return `<g fill="${gearDblFill}" stroke="${gearDblStroke}">
          <polygon points="${this.getGearPointsString(30, 30, 20)}" />
          <circle cx="30" cy="30" r="6" fill="#fff" />
          <polygon points="${this.getGearPointsString(68, 65, 24)}" />
          <circle cx="68" cy="65" r="7" fill="#fff" />
        </g>`;

      case 'bearing':
        const bearingFill = fillColor !== 'none' ? fillColor : '#e0e0e0';
        const bearingStroke = strokeColor || '#666';
        return `<g fill="none" stroke="${bearingStroke}" stroke-width="2">
          <circle cx="50" cy="50" r="45" fill="${bearingFill}" />
          <circle cx="50" cy="50" r="20" fill="#fff" />
          <circle cx="50" cy="12" r="7" fill="#999" />
          <circle cx="83" cy="30" r="7" fill="#999" />
          <circle cx="83" cy="70" r="7" fill="#999" />
          <circle cx="50" cy="88" r="7" fill="#999" />
          <circle cx="17" cy="70" r="7" fill="#999" />
          <circle cx="17" cy="30" r="7" fill="#999" />
        </g>`;

      case 'spring':
        const springStroke = strokeColor || '#333';
        return `<g fill="none" stroke="${springStroke}" stroke-width="3">
          <line x1="20" y1="5" x2="80" y2="5" />
          <path d="M 50 5 C 80 5, 80 18, 50 18 S 20 31, 50 31 S 80 44, 50 44 S 20 57, 50 57 S 80 70, 50 70 S 20 83, 50 83 S 80 95, 50 95" />
          <line x1="20" y1="95" x2="80" y2="95" />
        </g>`;

      case 'bolt':
        const boltFill = fillColor !== 'none' ? fillColor : '#888';
        const boltStroke = strokeColor || '#555';
        return `<g fill="${boltFill}" stroke="${boltStroke}" stroke-width="1">
          <polygon points="30,0 70,0 85,20 70,40 30,40 15,20" />
          <rect x="35" y="40" width="30" height="60" />
          <line x1="35" y1="55" x2="65" y2="55" stroke-width="2" />
          <line x1="35" y1="68" x2="65" y2="68" stroke-width="2" />
          <line x1="35" y1="81" x2="65" y2="81" stroke-width="2" />
          <line x1="35" y1="94" x2="65" y2="94" stroke-width="2" />
        </g>`;

      case 'nut':
        const nutFill = fillColor !== 'none' ? fillColor : '#888';
        const nutStroke = strokeColor || '#555';
        return `<g fill="${nutFill}" stroke="${nutStroke}" stroke-width="2">
          <polygon points="50,5 90,27 90,73 50,95 10,73 10,27" />
          <circle cx="50" cy="50" r="20" fill="#fff" />
        </g>`;

      case 'piston':
        const pistonFill = fillColor !== 'none' ? fillColor : '#ccc';
        const pistonStroke = strokeColor || '#666';
        return `<g fill="${pistonFill}" stroke="${pistonStroke}" stroke-width="2">
          <rect x="10" y="0" width="80" height="25" rx="3" />
          <rect x="35" y="25" width="30" height="20" />
          <ellipse cx="50" cy="62" rx="40" ry="8" />
          <rect x="10" y="62" width="80" height="35" stroke="none" />
          <ellipse cx="50" cy="97" rx="40" ry="3" fill="#999" />
        </g>`;

      // ═══════════════ TEKNİK ÇİZİM ═══════════════
      case 'centerline':
        const clStroke = strokeColor || '#c00';
        return `<g fill="none" stroke="${clStroke}" stroke-width="1" stroke-dasharray="15,5,3,5">
          <line x1="0" y1="50" x2="100" y2="50" />
          <line x1="50" y1="0" x2="50" y2="100" />
        </g>`;

      case 'dimension-line':
        const dimStroke = strokeColor || '#000';
        return `<g fill="none" stroke="${dimStroke}" stroke-width="1.5">
          <line x1="5" y1="0" x2="5" y2="100" />
          <line x1="95" y1="0" x2="95" y2="100" />
          <line x1="5" y1="50" x2="95" y2="50" />
          <polygon points="5,45 5,55 15,50" fill="${dimStroke}" stroke="none" />
          <polygon points="95,45 95,55 85,50" fill="${dimStroke}" stroke="none" />
        </g>`;

      case 'section-marker':
        const secStroke = strokeColor || '#000';
        const secFill = fillColor !== 'none' ? fillColor : '#fff';
        return `<g stroke="${secStroke}" stroke-width="2">
          <line x1="10" y1="50" x2="90" y2="50" stroke-dasharray="8,4" />
          <circle cx="10" cy="50" r="12" fill="${secFill}" />
          <circle cx="90" cy="50" r="12" fill="${secFill}" />
          <text x="10" y="55" text-anchor="middle" font-size="14" font-weight="bold" fill="#000">A</text>
          <text x="90" y="55" text-anchor="middle" font-size="14" font-weight="bold" fill="#000">A</text>
        </g>`;

      case 'datum':
        const datumStroke = strokeColor || '#000';
        const datumFill = fillColor !== 'none' ? fillColor : '#fff';
        return `<g stroke="${datumStroke}" stroke-width="2">
          <polygon points="50,15 30,85 70,85" fill="${datumFill}" />
          <line x1="20" y1="85" x2="80" y2="85" />
          <text x="50" y="62" text-anchor="middle" font-size="24" font-weight="bold" fill="#000">A</text>
        </g>`;

      case 'surface-finish':
        const sfStroke = strokeColor || '#000';
        return `<g stroke="${sfStroke}" stroke-width="2" fill="none">
          <path d="M 10 95 L 50 20 L 90 95" />
          <line x1="30" y1="95" x2="70" y2="95" />
          <text x="50" y="55" text-anchor="middle" font-size="14" fill="${sfStroke}">Ra</text>
        </g>`;

      // ═══════════════ UYARI / GÜVENLİK ═══════════════
      case 'warning-triangle':
        const warnFill = fillColor !== 'none' ? fillColor : '#f1c40f';
        const warnStroke = strokeColor || '#000';
        return `<g>
          <polygon points="50,5 95,90 5,90" fill="${warnFill}" stroke="${warnStroke}" stroke-width="3" />
          <text x="50" y="75" text-anchor="middle" font-size="50" font-weight="bold" fill="#000">!</text>
        </g>`;

      case 'danger-sign':
        const dangerFill = fillColor !== 'none' ? fillColor : '#e74c3c';
        return `<g>
          <circle cx="50" cy="50" r="45" fill="${dangerFill}" stroke="#fff" stroke-width="4" />
          <text x="50" y="70" text-anchor="middle" font-size="55" font-weight="bold" fill="#fff">✕</text>
        </g>`;

      case 'prohibition':
        return `<g>
          <circle cx="50" cy="50" r="45" fill="none" stroke="#c0392b" stroke-width="8" />
          <line x1="18" y1="18" x2="82" y2="82" stroke="#c0392b" stroke-width="8" />
        </g>`;

      case 'info-circle':
        const infoFill = fillColor !== 'none' ? fillColor : '#3498db';
        return `<g>
          <circle cx="50" cy="50" r="45" fill="${infoFill}" stroke="none" />
          <text x="50" y="72" text-anchor="middle" font-size="55" font-weight="bold" fill="#fff">i</text>
        </g>`;

      case 'mandatory':
        const mandFill = fillColor !== 'none' ? fillColor : '#2980b9';
        return `<g>
          <circle cx="50" cy="50" r="45" fill="${mandFill}" stroke="none" />
          <text x="50" y="72" text-anchor="middle" font-size="50" font-weight="bold" fill="#fff">!</text>
        </g>`;

      // ═══════════════ KALİTE KONTROL ═══════════════
      case 'quality-stamp':
        const qsStroke = strokeColor || '#27ae60';
        return `<g>
          <circle cx="50" cy="50" r="45" fill="none" stroke="${qsStroke}" stroke-width="4" />
          <circle cx="50" cy="50" r="35" fill="none" stroke="${qsStroke}" stroke-width="2" />
          <text x="50" y="40" text-anchor="middle" font-size="12" fill="${qsStroke}">KALİTE</text>
          <text x="50" y="62" text-anchor="middle" font-size="18" font-weight="bold" fill="${qsStroke}">OK</text>
        </g>`;

      case 'inspection-mark':
        const imStroke = strokeColor || '#27ae60';
        const imFill = fillColor !== 'none' ? fillColor : '#fff';
        return `<g>
          <rect x="5" y="5" width="90" height="90" fill="${imFill}" stroke="${imStroke}" stroke-width="3" rx="5" />
          <polyline points="20,50 42,72 80,28" fill="none" stroke="#27ae60" stroke-width="8" stroke-linecap="round" stroke-linejoin="round" />
        </g>`;

      // ═══════════════ AKIŞ DİYAGRAMI ═══════════════
      case 'process-box':
        const pbFill = fillColor !== 'none' ? fillColor : '#e8f4fd';
        const pbStroke = strokeColor || '#3498db';
        return `<rect x="5" y="20" width="90" height="60" rx="3" fill="${pbFill}" stroke="${pbStroke}" stroke-width="2" />`;

      case 'decision':
        const decFill = fillColor !== 'none' ? fillColor : '#fef9e7';
        const decStroke = strokeColor || '#f39c12';
        return `<polygon points="50,5 95,50 50,95 5,50" fill="${decFill}" stroke="${decStroke}" stroke-width="2" />`;

      case 'connector':
        const conFill = fillColor !== 'none' ? fillColor : '#d5f5e3';
        const conStroke = strokeColor || '#27ae60';
        return `<circle cx="50" cy="50" r="35" fill="${conFill}" stroke="${conStroke}" stroke-width="2" />`;

      case 'document':
        const docFill = fillColor !== 'none' ? fillColor : '#fff';
        const docStroke = strokeColor || '#666';
        return `<path d="M 5 10 L 95 10 L 95 80 Q 75 95 50 80 Q 25 65 5 80 Z" fill="${docFill}" stroke="${docStroke}" stroke-width="2" />`;

      case 'database':
        const dbFill = fillColor !== 'none' ? fillColor : '#ebdef0';
        const dbStroke = strokeColor || '#8e44ad';
        return `<g fill="${dbFill}" stroke="${dbStroke}" stroke-width="2">
          <ellipse cx="50" cy="18" rx="42" ry="15" />
          <rect x="8" y="18" width="84" height="60" stroke="none" />
          <ellipse cx="50" cy="78" rx="42" ry="15" />
          <line x1="8" y1="18" x2="8" y2="78" />
          <line x1="92" y1="18" x2="92" y2="78" />
        </g>`;

      case 'terminator':
        const termFill = fillColor !== 'none' ? fillColor : '#fadbd8';
        const termStroke = strokeColor || '#e74c3c';
        return `<rect x="5" y="25" width="90" height="50" rx="25" fill="${termFill}" stroke="${termStroke}" stroke-width="2" />`;

      // ═══════════════ DİĞER SİMGELER ═══════════════
      case 'tag':
        const tagFill = fillColor !== 'none' ? fillColor : '#fdebd0';
        const tagStroke = strokeColor || '#e67e22';
        return `<polygon points="15,10 85,10 95,50 85,90 15,90 15,50" fill="${tagFill}" stroke="${tagStroke}" stroke-width="2" />`;

      case 'flag':
        const flagFill = fillColor !== 'none' ? fillColor : '#e74c3c';
        const flagStroke = strokeColor || '#333';
        return `<g stroke="${flagStroke}" stroke-width="2">
          <line x1="15" y1="5" x2="15" y2="95" />
          <path d="M 15 10 Q 45 5, 45 25 Q 45 45, 85 40 L 85 60 Q 45 65, 45 45 Q 45 25, 15 30 Z" fill="${flagFill}" />
        </g>`;

      case 'pin':
        const pinFill = fillColor !== 'none' ? fillColor : '#e74c3c';
        const pinStroke = strokeColor || '#c0392b';
        return `<g fill="${pinFill}" stroke="${pinStroke}" stroke-width="1">
          <circle cx="50" cy="30" r="25" />
          <polygon points="38,50 62,50 50,95" fill="#888" stroke="#555" />
        </g>`;

      default:
        return '<rect x="0" y="0" width="100" height="100" />';
    }
  }

  /**
   * Düzenli çokgen noktalarını string olarak döndür (SVG için)
   */
  private getRegularPolygonPointsString(sides: number): string {
    const cx = 50, cy = 50, r = 45;
    const points: string[] = [];
    const angleStep = (2 * Math.PI) / sides;
    const startAngle = -Math.PI / 2;

    for (let i = 0; i < sides; i++) {
      const angle = startAngle + i * angleStep;
      const x = cx + r * Math.cos(angle);
      const y = cy + r * Math.sin(angle);
      points.push(`${x.toFixed(1)},${y.toFixed(1)}`);
    }

    return points.join(' ');
  }

  /**
   * Yıldız noktalarını string olarak döndür (SVG için)
   */
  private getStarPointsString(numPoints: number, innerRadiusRatio: number): string {
    const cx = 50, cy = 50, outerR = 45;
    const innerR = outerR * innerRadiusRatio;
    const points: string[] = [];
    const angleStep = Math.PI / numPoints;
    const startAngle = -Math.PI / 2;

    for (let i = 0; i < numPoints * 2; i++) {
      const angle = startAngle + i * angleStep;
      const r = i % 2 === 0 ? outerR : innerR;
      const x = cx + r * Math.cos(angle);
      const y = cy + r * Math.sin(angle);
      points.push(`${x.toFixed(1)},${y.toFixed(1)}`);
    }

    return points.join(' ');
  }

  /**
   * Dişli çark noktalarını string olarak döndür (SVG için)
   */
  private getGearPointsString(cx: number, cy: number, r: number): string {
    const teeth = 8;
    const innerR = r * 0.7;
    const points: string[] = [];
    const angleStep = Math.PI / teeth;

    for (let i = 0; i < teeth * 2; i++) {
      const angle = -Math.PI / 2 + i * angleStep;
      const currentR = i % 2 === 0 ? r : innerR;
      const x = cx + currentR * Math.cos(angle);
      const y = cy + currentR * Math.sin(angle);
      points.push(`${x.toFixed(1)},${y.toFixed(1)}`);
    }

    return points.join(' ');
  }

  // ============ SHAPE (LEGACY FALLBACK) ============

  private renderShape(doc: jsPDF, element: PdfElement, x: number, y: number): void {
    const props = element.properties;
    const shapeType = props.shapeType || 'rect';
    const strokeColor = props.strokeColor || '#000000';
    const strokeWidth = props.strokeWidth || 1;
    const fillColor = props.fillColor;
    const lineStyle = props.lineStyle || 'solid';

    doc.setDrawColor(strokeColor);
    doc.setLineWidth(strokeWidth);

    // Çizgi stili ayarı
    if (lineStyle === 'dashed') {
      (doc as any).setLineDashPattern([3, 2], 0);
    } else if (lineStyle === 'dotted') {
      (doc as any).setLineDashPattern([1, 1], 0);
    } else {
      (doc as any).setLineDashPattern([], 0);
    }

    if (fillColor) {
      doc.setFillColor(fillColor);
    }

    const drawMode = fillColor ? 'FD' : 'S';
    const w = element.width;
    const h = element.height;
    const cx = x + w / 2;
    const cy = y + h / 2;

    switch (shapeType) {
      case 'rect':
        doc.rect(x, y, w, h, drawMode);
        break;

      case 'rounded-rect':
        const cornerRadius = props.cornerRadius || 5;
        (doc as any).roundedRect(x, y, w, h, cornerRadius, cornerRadius, drawMode);
        break;

      case 'circle':
        const radius = Math.min(w, h) / 2;
        doc.circle(cx, cy, radius, drawMode);
        break;

      case 'ellipse':
        doc.ellipse(cx, cy, w / 2, h / 2, drawMode);
        break;

      case 'line':
        doc.line(x, y, x + w, y + h);
        break;

      case 'triangle':
        this.drawPolygon(doc, [
          { x: cx, y: y },                    // Üst nokta
          { x: x + w, y: y + h },             // Sağ alt
          { x: x, y: y + h }                  // Sol alt
        ], drawMode, fillColor);
        break;

      case 'diamond':
        this.drawPolygon(doc, [
          { x: cx, y: y },                    // Üst
          { x: x + w, y: cy },                // Sağ
          { x: cx, y: y + h },                // Alt
          { x: x, y: cy }                     // Sol
        ], drawMode, fillColor);
        break;

      case 'pentagon':
        this.drawRegularPolygon(doc, cx, cy, Math.min(w, h) / 2, 5, drawMode, fillColor);
        break;

      case 'hexagon':
        this.drawRegularPolygon(doc, cx, cy, Math.min(w, h) / 2, 6, drawMode, fillColor);
        break;

      case 'star':
        const starPoints = props.starPoints || 5;
        const innerRadius = props.starInnerRadius || 0.4;
        this.drawStar(doc, cx, cy, Math.min(w, h) / 2, starPoints, innerRadius, drawMode, fillColor);
        break;

      case 'arrow':
        this.drawArrow(doc, x, y, w, h, props.arrowHeadSize || 4, props.arrowStyle || 'simple', fillColor);
        break;

      case 'double-arrow':
        this.drawDoubleArrow(doc, x, y, w, h, props.arrowHeadSize || 4, props.arrowStyle || 'simple', fillColor);
        break;

      case 'cross':
        const crossThickness = Math.min(w, h) / 3;
        // Yatay çubuk
        doc.rect(x, cy - crossThickness / 2, w, crossThickness, drawMode);
        // Dikey çubuk
        doc.rect(cx - crossThickness / 2, y, crossThickness, h, drawMode);
        break;

      case 'semicircle':
        // Yarım daire (üst yarı)
        this.drawArc(doc, cx, y + h, Math.min(w, h) / 2, Math.PI, 0, drawMode, fillColor);
        break;

      case 'quarter-circle':
        // Çeyrek daire (sol üst)
        this.drawArc(doc, x + w, y + h, Math.min(w, h), Math.PI, Math.PI * 1.5, drawMode, fillColor);
        break;

      case 'right-triangle':
        // Dik üçgen
        this.drawPolygon(doc, [
          { x: x, y: y },           // Sol üst (dik açı)
          { x: x + w, y: y + h },   // Sağ alt
          { x: x, y: y + h }        // Sol alt
        ], drawMode, fillColor);
        break;

      case 'parallelogram':
        const skew = w * 0.2;
        this.drawPolygon(doc, [
          { x: x + skew, y: y },       // Sol üst
          { x: x + w, y: y },          // Sağ üst
          { x: x + w - skew, y: y + h }, // Sağ alt
          { x: x, y: y + h }           // Sol alt
        ], drawMode, fillColor);
        break;

      case 'trapezoid':
        const indent = w * 0.15;
        this.drawPolygon(doc, [
          { x: x + indent, y: y },     // Sol üst
          { x: x + w - indent, y: y }, // Sağ üst
          { x: x + w, y: y + h },      // Sağ alt
          { x: x, y: y + h }           // Sol alt
        ], drawMode, fillColor);
        break;

      case 'octagon':
        this.drawRegularPolygon(doc, cx, cy, Math.min(w, h) / 2, 8, drawMode, fillColor);
        break;

      case 'heart':
        this.drawHeart(doc, cx, cy, Math.min(w, h) / 2, drawMode, fillColor);
        break;

      case 'x-mark':
        const xThick = Math.min(w, h) / 4;
        // Sol üstten sağ alta
        doc.line(x, y, x + w, y + h);
        doc.line(x + xThick, y, x + w, y + h - xThick);
        // Sağ üstten sol alta
        doc.line(x + w, y, x, y + h);
        doc.line(x + w - xThick, y, x, y + h - xThick);
        break;

      case 'checkmark':
        this.drawCheckmark(doc, x, y, w, h, strokeWidth);
        break;

      case 'lightning':
        this.drawLightning(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'cloud':
        this.drawCloud(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'speech-bubble':
        this.drawSpeechBubble(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'gear':
        this.drawGear(doc, cx, cy, Math.min(w, h) / 2, 8, drawMode, fillColor);
        break;

      case 'curved-arrow':
        this.drawCurvedArrow(doc, x, y, w, h, props.arrowHeadSize || 4, fillColor);
        break;

      case 'chevron':
        this.drawChevron(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'bracket-left':
        this.drawBracket(doc, x, y, w, h, 'left');
        break;

      case 'bracket-right':
        this.drawBracket(doc, x, y, w, h, 'right');
        break;

      case 'brace-left':
        this.drawBrace(doc, x, y, w, h, 'left');
        break;

      case 'brace-right':
        this.drawBrace(doc, x, y, w, h, 'right');
        break;

      // ═══════════════ YENİ AÇILI AYRAÇLAR ═══════════════
      case 'angle-bracket-l':
        this.drawAngleBracket(doc, x, y, w, h, 'left');
        break;

      case 'angle-bracket-r':
        this.drawAngleBracket(doc, x, y, w, h, 'right');
        break;

      // ═══════════════ YENİ OKLAR ═══════════════
      case 'block-arrow':
        this.drawBlockArrow(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'bent-arrow':
        this.drawBentArrow(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'circular-arrow':
        this.drawCircularArrow(doc, x, y, w, h, drawMode, fillColor);
        break;

      // ═══════════════ ENDÜSTRİYEL SİMGELER ═══════════════
      case 'gear-double':
        this.drawGearDouble(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'bearing':
        this.drawBearing(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'spring':
        this.drawSpring(doc, x, y, w, h);
        break;

      case 'bolt':
        this.drawBolt(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'nut':
        this.drawNut(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'piston':
        this.drawPiston(doc, x, y, w, h, drawMode, fillColor);
        break;

      // ═══════════════ TEKNİK ÇİZİM ═══════════════
      case 'centerline':
        this.drawCenterline(doc, x, y, w, h);
        break;

      case 'dimension-line':
        this.drawDimensionLine(doc, x, y, w, h);
        break;

      case 'section-marker':
        this.drawSectionMarker(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'datum':
        this.drawDatum(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'surface-finish':
        this.drawSurfaceFinish(doc, x, y, w, h);
        break;

      // ═══════════════ UYARI / GÜVENLİK ═══════════════
      case 'warning-triangle':
        this.drawWarningTriangle(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'danger-sign':
        this.drawDangerSign(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'prohibition':
        this.drawProhibition(doc, x, y, w, h);
        break;

      case 'info-circle':
        this.drawInfoCircle(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'mandatory':
        this.drawMandatory(doc, x, y, w, h, drawMode, fillColor);
        break;

      // ═══════════════ KALİTE KONTROL ═══════════════
      case 'quality-stamp':
        this.drawQualityStamp(doc, x, y, w, h);
        break;

      case 'inspection-mark':
        this.drawInspectionMark(doc, x, y, w, h, drawMode, fillColor);
        break;

      // ═══════════════ AKIŞ DİYAGRAMI ═══════════════
      case 'process-box':
        this.drawProcessBox(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'decision':
        this.drawDecision(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'connector':
        this.drawConnector(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'document':
        this.drawDocument(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'database':
        this.drawDatabase(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'terminator':
        this.drawTerminator(doc, x, y, w, h, drawMode, fillColor);
        break;

      // ═══════════════ DİĞER SİMGELER ═══════════════
      case 'tag':
        this.drawTag(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'flag':
        this.drawFlag(doc, x, y, w, h, drawMode, fillColor);
        break;

      case 'pin':
        this.drawPin(doc, x, y, w, h, drawMode, fillColor);
        break;
    }

    // Çizgi stilini sıfırla
    (doc as any).setLineDashPattern([], 0);
  }

  // Polygon çizimi (dolu veya çizgili)
  private drawPolygon(
    doc: jsPDF,
    points: { x: number; y: number }[],
    drawMode: string,
    fillColor?: string
  ): void {
    if (points.length < 3) return;

    if (fillColor && drawMode.includes('F')) {
      // Dolu polygon - jsPDF triangle veya lines kullanarak
      doc.setFillColor(fillColor);
      const path = points.map((p, i) => {
        if (i === 0) return `${p.x} ${p.y} m`;
        return `${p.x} ${p.y} l`;
      }).join(' ') + ' h';
      // jsPDF internal path fonksiyonunu kullan
      (doc as any).internal.write(path + (drawMode === 'FD' ? ' B' : ' f'));
    } else {
      // Sadece çizgi
      for (let i = 0; i < points.length; i++) {
        const p1 = points[i];
        const p2 = points[(i + 1) % points.length];
        doc.line(p1.x, p1.y, p2.x, p2.y);
      }
    }
  }

  // Düzenli çokgen çizimi (beşgen, altıgen vb.)
  private drawRegularPolygon(
    doc: jsPDF,
    cx: number,
    cy: number,
    radius: number,
    sides: number,
    drawMode: string,
    fillColor?: string
  ): void {
    const points: { x: number; y: number }[] = [];
    const angleStep = (2 * Math.PI) / sides;
    const startAngle = -Math.PI / 2; // Üstten başla

    for (let i = 0; i < sides; i++) {
      const angle = startAngle + i * angleStep;
      points.push({
        x: cx + radius * Math.cos(angle),
        y: cy + radius * Math.sin(angle)
      });
    }

    this.drawPolygon(doc, points, drawMode, fillColor);
  }

  // Yıldız çizimi
  private drawStar(
    doc: jsPDF,
    cx: number,
    cy: number,
    radius: number,
    points: number,
    innerRadiusRatio: number,
    drawMode: string,
    fillColor?: string
  ): void {
    const starPoints: { x: number; y: number }[] = [];
    const innerRadius = radius * innerRadiusRatio;
    const angleStep = Math.PI / points;
    const startAngle = -Math.PI / 2;

    for (let i = 0; i < points * 2; i++) {
      const angle = startAngle + i * angleStep;
      const r = i % 2 === 0 ? radius : innerRadius;
      starPoints.push({
        x: cx + r * Math.cos(angle),
        y: cy + r * Math.sin(angle)
      });
    }

    this.drawPolygon(doc, starPoints, drawMode, fillColor);
  }

  // Ok çizimi
  private drawArrow(
    doc: jsPDF,
    x: number,
    y: number,
    w: number,
    h: number,
    headSize: number,
    style: string,
    fillColor?: string
  ): void {
    const cy = y + h / 2;
    const endX = x + w;

    // Ana çizgi
    doc.line(x, cy, endX - headSize, cy);

    // Ok başı
    if (style === 'filled' && fillColor) {
      doc.setFillColor(fillColor);
      this.drawPolygon(doc, [
        { x: endX, y: cy },
        { x: endX - headSize * 1.5, y: cy - headSize },
        { x: endX - headSize * 1.5, y: cy + headSize }
      ], 'F', fillColor);
    } else {
      doc.line(endX - headSize * 1.5, cy - headSize, endX, cy);
      doc.line(endX - headSize * 1.5, cy + headSize, endX, cy);
    }
  }

  // Çift yönlü ok çizimi
  private drawDoubleArrow(
    doc: jsPDF,
    x: number,
    y: number,
    w: number,
    h: number,
    headSize: number,
    style: string,
    fillColor?: string
  ): void {
    const cy = y + h / 2;
    const startX = x + headSize * 1.5;
    const endX = x + w - headSize * 1.5;

    // Ana çizgi
    doc.line(startX, cy, endX, cy);

    // Sağ ok başı
    if (style === 'filled' && fillColor) {
      doc.setFillColor(fillColor);
      this.drawPolygon(doc, [
        { x: x + w, y: cy },
        { x: endX, y: cy - headSize },
        { x: endX, y: cy + headSize }
      ], 'F', fillColor);
      // Sol ok başı
      this.drawPolygon(doc, [
        { x: x, y: cy },
        { x: startX, y: cy - headSize },
        { x: startX, y: cy + headSize }
      ], 'F', fillColor);
    } else {
      // Sağ ok
      doc.line(endX, cy - headSize, x + w, cy);
      doc.line(endX, cy + headSize, x + w, cy);
      // Sol ok
      doc.line(startX, cy - headSize, x, cy);
      doc.line(startX, cy + headSize, x, cy);
    }
  }

  // Yay (Arc) çizimi
  private drawArc(
    doc: jsPDF,
    cx: number,
    cy: number,
    radius: number,
    startAngle: number,
    endAngle: number,
    drawMode: string,
    fillColor?: string
  ): void {
    const points: { x: number; y: number }[] = [];
    const steps = 32;
    const angleRange = endAngle - startAngle;

    for (let i = 0; i <= steps; i++) {
      const angle = startAngle + (i / steps) * angleRange;
      points.push({
        x: cx + radius * Math.cos(angle),
        y: cy + radius * Math.sin(angle)
      });
    }

    // Merkeze dön (kapalı şekil için)
    if (drawMode.includes('F')) {
      points.push({ x: cx, y: cy });
    }

    this.drawPolygon(doc, points, drawMode, fillColor);
  }

  // Kalp çizimi
  private drawHeart(
    doc: jsPDF,
    cx: number,
    cy: number,
    size: number,
    drawMode: string,
    fillColor?: string
  ): void {
    const points: { x: number; y: number }[] = [];
    const steps = 50;

    for (let i = 0; i <= steps; i++) {
      const t = (i / steps) * 2 * Math.PI;
      // Kalp parametrik denklemi
      const x = 16 * Math.pow(Math.sin(t), 3);
      const y = 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);
      points.push({
        x: cx + (x / 16) * size,
        y: cy - (y / 16) * size
      });
    }

    this.drawPolygon(doc, points, drawMode, fillColor);
  }

  // Onay işareti çizimi
  private drawCheckmark(
    doc: jsPDF,
    x: number,
    y: number,
    w: number,
    h: number,
    strokeWidth: number
  ): void {
    doc.setLineWidth(strokeWidth * 1.5);
    // Sol alt köşeden ortaya
    doc.line(x + w * 0.15, y + h * 0.5, x + w * 0.4, y + h * 0.75);
    // Ortadan sağ üst köşeye
    doc.line(x + w * 0.4, y + h * 0.75, x + w * 0.85, y + h * 0.2);
  }

  // Şimşek çizimi
  private drawLightning(
    doc: jsPDF,
    x: number,
    y: number,
    w: number,
    h: number,
    drawMode: string,
    fillColor?: string
  ): void {
    this.drawPolygon(doc, [
      { x: x + w * 0.55, y: y },
      { x: x + w * 0.25, y: y + h * 0.45 },
      { x: x + w * 0.5, y: y + h * 0.45 },
      { x: x + w * 0.35, y: y + h },
      { x: x + w * 0.75, y: y + h * 0.5 },
      { x: x + w * 0.5, y: y + h * 0.5 },
      { x: x + w * 0.7, y: y }
    ], drawMode, fillColor);
  }

  // Bulut çizimi
  private drawCloud(
    doc: jsPDF,
    x: number,
    y: number,
    w: number,
    h: number,
    drawMode: string,
    fillColor?: string
  ): void {
    // Basit bulut - üst üste daireler
    const circles = [
      { cx: x + w * 0.25, cy: y + h * 0.6, r: w * 0.2 },
      { cx: x + w * 0.45, cy: y + h * 0.4, r: w * 0.25 },
      { cx: x + w * 0.7, cy: y + h * 0.5, r: w * 0.22 },
      { cx: x + w * 0.5, cy: y + h * 0.65, r: w * 0.2 }
    ];

    if (fillColor) {
      doc.setFillColor(fillColor);
    }

    for (const c of circles) {
      doc.circle(c.cx, c.cy, c.r, drawMode);
    }
  }

  // Konuşma balonu çizimi
  private drawSpeechBubble(
    doc: jsPDF,
    x: number,
    y: number,
    w: number,
    h: number,
    drawMode: string,
    fillColor?: string
  ): void {
    const bubbleH = h * 0.75;
    const cornerR = Math.min(w, bubbleH) * 0.15;

    // Ana balon (yuvarlatılmış dikdörtgen)
    (doc as any).roundedRect(x, y, w, bubbleH, cornerR, cornerR, drawMode);

    // Kuyruk (üçgen)
    this.drawPolygon(doc, [
      { x: x + w * 0.2, y: y + bubbleH - 1 },
      { x: x + w * 0.1, y: y + h },
      { x: x + w * 0.35, y: y + bubbleH - 1 }
    ], drawMode, fillColor);
  }

  // Dişli çizimi
  private drawGear(
    doc: jsPDF,
    cx: number,
    cy: number,
    radius: number,
    teeth: number,
    drawMode: string,
    fillColor?: string
  ): void {
    const points: { x: number; y: number }[] = [];
    const outerRadius = radius;
    const innerRadius = radius * 0.7;
    const toothWidth = Math.PI / teeth / 2;

    for (let i = 0; i < teeth; i++) {
      const baseAngle = (i / teeth) * 2 * Math.PI - Math.PI / 2;

      // Diş tabanı sol
      points.push({
        x: cx + innerRadius * Math.cos(baseAngle - toothWidth),
        y: cy + innerRadius * Math.sin(baseAngle - toothWidth)
      });
      // Diş üst sol
      points.push({
        x: cx + outerRadius * Math.cos(baseAngle - toothWidth * 0.5),
        y: cy + outerRadius * Math.sin(baseAngle - toothWidth * 0.5)
      });
      // Diş üst sağ
      points.push({
        x: cx + outerRadius * Math.cos(baseAngle + toothWidth * 0.5),
        y: cy + outerRadius * Math.sin(baseAngle + toothWidth * 0.5)
      });
      // Diş tabanı sağ
      points.push({
        x: cx + innerRadius * Math.cos(baseAngle + toothWidth),
        y: cy + innerRadius * Math.sin(baseAngle + toothWidth)
      });
    }

    this.drawPolygon(doc, points, drawMode, fillColor);

    // İç daire (delik)
    const holeRadius = radius * 0.25;
    doc.setFillColor('#ffffff');
    doc.circle(cx, cy, holeRadius, 'F');
    doc.circle(cx, cy, holeRadius, 'S');
  }

  // Eğri ok çizimi
  private drawCurvedArrow(
    doc: jsPDF,
    x: number,
    y: number,
    w: number,
    h: number,
    headSize: number,
    fillColor?: string
  ): void {
    // Bezier eğrisi noktaları
    const startX = x;
    const startY = y + h * 0.7;
    const endX = x + w;
    const endY = y + h * 0.3;
    const cp1x = x + w * 0.3;
    const cp1y = y;
    const cp2x = x + w * 0.7;
    const cp2y = y;

    // Eğri çiz (yaklaşık)
    const steps = 20;
    for (let i = 0; i < steps; i++) {
      const t1 = i / steps;
      const t2 = (i + 1) / steps;

      const x1 = this.bezierPoint(startX, cp1x, cp2x, endX, t1);
      const y1 = this.bezierPoint(startY, cp1y, cp2y, endY, t1);
      const x2 = this.bezierPoint(startX, cp1x, cp2x, endX, t2);
      const y2 = this.bezierPoint(startY, cp1y, cp2y, endY, t2);

      doc.line(x1, y1, x2, y2);
    }

    // Ok başı
    doc.line(endX - headSize, endY - headSize, endX, endY);
    doc.line(endX - headSize, endY + headSize * 0.5, endX, endY);
  }

  private bezierPoint(p0: number, p1: number, p2: number, p3: number, t: number): number {
    const mt = 1 - t;
    return mt * mt * mt * p0 + 3 * mt * mt * t * p1 + 3 * mt * t * t * p2 + t * t * t * p3;
  }

  // Şerit ok (Chevron) çizimi
  private drawChevron(
    doc: jsPDF,
    x: number,
    y: number,
    w: number,
    h: number,
    drawMode: string,
    fillColor?: string
  ): void {
    const notch = w * 0.2;
    this.drawPolygon(doc, [
      { x: x, y: y },
      { x: x + w - notch, y: y },
      { x: x + w, y: y + h / 2 },
      { x: x + w - notch, y: y + h },
      { x: x, y: y + h },
      { x: x + notch, y: y + h / 2 }
    ], drawMode, fillColor);
  }

  // Köşeli ayraç çizimi
  private drawBracket(
    doc: jsPDF,
    x: number,
    y: number,
    w: number,
    h: number,
    side: 'left' | 'right'
  ): void {
    const bracketW = w * 0.3;
    if (side === 'left') {
      doc.line(x + bracketW, y, x, y);
      doc.line(x, y, x, y + h);
      doc.line(x, y + h, x + bracketW, y + h);
    } else {
      doc.line(x + w - bracketW, y, x + w, y);
      doc.line(x + w, y, x + w, y + h);
      doc.line(x + w, y + h, x + w - bracketW, y + h);
    }
  }

  // Süslü ayraç çizimi
  private drawBrace(
    doc: jsPDF,
    x: number,
    y: number,
    w: number,
    h: number,
    side: 'left' | 'right'
  ): void {
    const cx = side === 'left' ? x : x + w;
    const dir = side === 'left' ? 1 : -1;
    const curveW = w * 0.4;

    // Üst kıvrım
    doc.line(cx + dir * curveW, y, cx + dir * curveW * 0.5, y + h * 0.1);
    doc.line(cx + dir * curveW * 0.5, y + h * 0.1, cx, y + h * 0.25);
    doc.line(cx, y + h * 0.25, cx, y + h * 0.45);

    // Orta sivri
    doc.line(cx, y + h * 0.45, cx - dir * curveW * 0.3, y + h * 0.5);
    doc.line(cx - dir * curveW * 0.3, y + h * 0.5, cx, y + h * 0.55);

    // Alt kıvrım
    doc.line(cx, y + h * 0.55, cx, y + h * 0.75);
    doc.line(cx, y + h * 0.75, cx + dir * curveW * 0.5, y + h * 0.9);
    doc.line(cx + dir * curveW * 0.5, y + h * 0.9, cx + dir * curveW, y + h);
  }

  // ═══════════════ YENİ ŞEKİL ÇİZİM METODLARI ═══════════════

  // Açılı Ayraç < veya >
  private drawAngleBracket(doc: jsPDF, x: number, y: number, w: number, h: number, side: 'left' | 'right'): void {
    const margin = Math.min(w, h) * 0.1;
    if (side === 'left') {
      doc.line(x + w - margin, y + margin, x + margin, y + h / 2);
      doc.line(x + margin, y + h / 2, x + w - margin, y + h - margin);
    } else {
      doc.line(x + margin, y + margin, x + w - margin, y + h / 2);
      doc.line(x + w - margin, y + h / 2, x + margin, y + h - margin);
    }
  }

  // Blok Ok
  private drawBlockArrow(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    const points = [
      { x: x, y: y + h * 0.3 },
      { x: x + w * 0.6, y: y + h * 0.3 },
      { x: x + w * 0.6, y: y + h * 0.1 },
      { x: x + w, y: y + h * 0.5 },
      { x: x + w * 0.6, y: y + h * 0.9 },
      { x: x + w * 0.6, y: y + h * 0.7 },
      { x: x, y: y + h * 0.7 }
    ];
    this.drawPolygon(doc, points, drawMode, fillColor || '#3498db');
  }

  // Kıvrımlı Ok (90 derece)
  private drawBentArrow(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    const points = [
      { x: x + w * 0.1, y: y + h * 0.9 },
      { x: x + w * 0.1, y: y + h * 0.4 },
      { x: x + w * 0.5, y: y + h * 0.4 },
      { x: x + w * 0.5, y: y + h * 0.2 },
      { x: x + w * 0.9, y: y + h * 0.5 },
      { x: x + w * 0.5, y: y + h * 0.8 },
      { x: x + w * 0.5, y: y + h * 0.6 },
      { x: x + w * 0.3, y: y + h * 0.6 },
      { x: x + w * 0.3, y: y + h * 0.9 }
    ];
    this.drawPolygon(doc, points, drawMode, fillColor || '#3498db');
  }

  // Döngüsel Ok
  private drawCircularArrow(doc: jsPDF, x: number, y: number, w: number, h: number, _drawMode: string, fillColor?: string): void {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const r = Math.min(w, h) * 0.35;
    // Yarım daire çiz (sadece çizgi - 'S' stroke mode)
    this.drawArc(doc, cx, cy, r, 0.8 * Math.PI, 2.3 * Math.PI, 'S');
    // Ok başı
    const arrowX = cx + r * Math.cos(0.8 * Math.PI);
    const arrowY = cy + r * Math.sin(0.8 * Math.PI);
    const arrowSize = r * 0.35;
    const points = [
      { x: arrowX, y: arrowY - arrowSize },
      { x: arrowX - arrowSize * 0.7, y: arrowY + arrowSize * 0.5 },
      { x: arrowX + arrowSize * 0.7, y: arrowY + arrowSize * 0.5 }
    ];
    this.drawPolygon(doc, points, 'F', fillColor);
  }

  // Çift Dişli
  private drawGearDouble(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    // Sol üst küçük dişli
    this.drawGearAt(doc, x + w * 0.3, y + h * 0.3, Math.min(w, h) * 0.28, 6, drawMode, fillColor || '#95a5a6');
    // Sağ alt büyük dişli
    this.drawGearAt(doc, x + w * 0.68, y + h * 0.65, Math.min(w, h) * 0.32, 8, drawMode, fillColor || '#95a5a6');
  }

  // Parametrik dişli çizimi
  private drawGearAt(doc: jsPDF, cx: number, cy: number, radius: number, teeth: number, drawMode: string, fillColor?: string): void {
    const outerRadius = radius;
    const innerRadius = radius * 0.65;
    const toothWidth = Math.PI / teeth / 2;
    const points: { x: number; y: number }[] = [];

    for (let i = 0; i < teeth; i++) {
      const baseAngle = (i / teeth) * 2 * Math.PI - Math.PI / 2;
      points.push({ x: cx + innerRadius * Math.cos(baseAngle - toothWidth), y: cy + innerRadius * Math.sin(baseAngle - toothWidth) });
      points.push({ x: cx + outerRadius * Math.cos(baseAngle - toothWidth * 0.5), y: cy + outerRadius * Math.sin(baseAngle - toothWidth * 0.5) });
      points.push({ x: cx + outerRadius * Math.cos(baseAngle + toothWidth * 0.5), y: cy + outerRadius * Math.sin(baseAngle + toothWidth * 0.5) });
      points.push({ x: cx + innerRadius * Math.cos(baseAngle + toothWidth), y: cy + innerRadius * Math.sin(baseAngle + toothWidth) });
    }
    this.drawPolygon(doc, points, drawMode, fillColor);
    // İç delik
    doc.setFillColor('#ffffff');
    doc.circle(cx, cy, radius * 0.25, 'F');
  }

  // Rulman
  private drawBearing(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const r = Math.min(w, h) / 2 * 0.9;
    // Dış halka
    doc.setFillColor(fillColor || '#e0e0e0');
    doc.circle(cx, cy, r, 'FD');
    // İç halka
    doc.setFillColor('#ffffff');
    doc.circle(cx, cy, r * 0.45, 'FD');
    // Bilyeler
    const ballCount = 6;
    const ballR = r * 0.15;
    const ballOrbit = r * 0.72;
    doc.setFillColor('#999999');
    for (let i = 0; i < ballCount; i++) {
      const angle = (i / ballCount) * 2 * Math.PI - Math.PI / 2;
      doc.circle(cx + ballOrbit * Math.cos(angle), cy + ballOrbit * Math.sin(angle), ballR, 'FD');
    }
  }

  // Yay
  private drawSpring(doc: jsPDF, x: number, y: number, w: number, h: number): void {
    const coils = 6;
    const coilH = h / (coils + 1);
    const midX = x + w / 2;
    const amp = w * 0.35;
    // Üst çizgi
    doc.line(x + w * 0.2, y, x + w * 0.8, y);
    // Sarmallar
    for (let i = 0; i < coils; i++) {
      const startY = y + coilH * (i + 0.5);
      doc.line(midX - amp, startY, midX + amp, startY + coilH * 0.5);
      doc.line(midX + amp, startY + coilH * 0.5, midX - amp, startY + coilH);
    }
    // Alt çizgi
    doc.line(x + w * 0.2, y + h, x + w * 0.8, y + h);
  }

  // Cıvata
  private drawBolt(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    // Altıgen kafa
    const headH = h * 0.35;
    const headPoints = [
      { x: x + w * 0.3, y: y },
      { x: x + w * 0.7, y: y },
      { x: x + w * 0.85, y: y + headH * 0.5 },
      { x: x + w * 0.7, y: y + headH },
      { x: x + w * 0.3, y: y + headH },
      { x: x + w * 0.15, y: y + headH * 0.5 }
    ];
    this.drawPolygon(doc, headPoints, drawMode, fillColor || '#888888');
    // Gövde
    doc.setFillColor(fillColor || '#888888');
    doc.rect(x + w * 0.35, y + headH, w * 0.3, h - headH, drawMode);
    // Dişler
    const threadCount = 4;
    const threadH = (h - headH) / (threadCount + 1);
    for (let i = 1; i <= threadCount; i++) {
      doc.line(x + w * 0.35, y + headH + threadH * i, x + w * 0.65, y + headH + threadH * i);
    }
  }

  // Somun
  private drawNut(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const r = Math.min(w, h) / 2 * 0.9;
    // Altıgen
    const points: { x: number; y: number }[] = [];
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * 2 * Math.PI - Math.PI / 2;
      points.push({ x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) });
    }
    this.drawPolygon(doc, points, drawMode, fillColor || '#888888');
    // İç delik
    doc.setFillColor('#ffffff');
    doc.circle(cx, cy, r * 0.4, 'FD');
  }

  // Piston
  private drawPiston(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    doc.setFillColor(fillColor || '#cccccc');
    // Üst kapak
    doc.rect(x + w * 0.1, y, w * 0.8, h * 0.2, drawMode);
    // Biyel kolu
    doc.rect(x + w * 0.35, y + h * 0.2, w * 0.3, h * 0.2, drawMode);
    // Piston gövdesi
    doc.rect(x + w * 0.1, y + h * 0.45, w * 0.8, h * 0.5, drawMode);
    // Segman çizgileri
    doc.line(x + w * 0.1, y + h * 0.55, x + w * 0.9, y + h * 0.55);
    doc.line(x + w * 0.1, y + h * 0.7, x + w * 0.9, y + h * 0.7);
  }

  // Merkez Çizgisi
  private drawCenterline(doc: jsPDF, x: number, y: number, w: number, h: number): void {
    doc.setDrawColor('#cc0000');
    (doc as any).setLineDashPattern([3, 1, 1, 1], 0);
    // Yatay
    doc.line(x, y + h / 2, x + w, y + h / 2);
    // Dikey
    doc.line(x + w / 2, y, x + w / 2, y + h);
  }

  // Ölçü Çizgisi
  private drawDimensionLine(doc: jsPDF, x: number, y: number, w: number, h: number): void {
    const arrowSize = Math.min(w, h) * 0.1;
    // Uzantı çizgileri
    doc.line(x + w * 0.05, y, x + w * 0.05, y + h);
    doc.line(x + w * 0.95, y, x + w * 0.95, y + h);
    // Ana çizgi
    doc.line(x + w * 0.05, y + h / 2, x + w * 0.95, y + h / 2);
    // Sol ok
    doc.line(x + w * 0.05, y + h / 2, x + w * 0.05 + arrowSize, y + h / 2 - arrowSize * 0.5);
    doc.line(x + w * 0.05, y + h / 2, x + w * 0.05 + arrowSize, y + h / 2 + arrowSize * 0.5);
    // Sağ ok
    doc.line(x + w * 0.95, y + h / 2, x + w * 0.95 - arrowSize, y + h / 2 - arrowSize * 0.5);
    doc.line(x + w * 0.95, y + h / 2, x + w * 0.95 - arrowSize, y + h / 2 + arrowSize * 0.5);
  }

  // Kesit İşareti
  private drawSectionMarker(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    const r = Math.min(w, h) * 0.15;
    // Kesikli çizgi
    (doc as any).setLineDashPattern([2, 1], 0);
    doc.line(x + r, y + h / 2, x + w - r, y + h / 2);
    (doc as any).setLineDashPattern([], 0);
    // Sol daire
    doc.setFillColor(fillColor || '#ffffff');
    doc.circle(x + r, y + h / 2, r, 'FD');
    doc.setFontSize(r * 1.5);
    doc.text('A', x + r, y + h / 2 + r * 0.4, { align: 'center' });
    // Sağ daire
    doc.circle(x + w - r, y + h / 2, r, 'FD');
    doc.text('A', x + w - r, y + h / 2 + r * 0.4, { align: 'center' });
  }

  // Datum (Referans)
  private drawDatum(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    const cx = x + w / 2;
    // Üçgen
    const points = [
      { x: cx, y: y + h * 0.15 },
      { x: x + w * 0.3, y: y + h * 0.85 },
      { x: x + w * 0.7, y: y + h * 0.85 }
    ];
    this.drawPolygon(doc, points, drawMode, fillColor || '#ffffff');
    // Alt çizgi
    doc.line(x + w * 0.2, y + h * 0.85, x + w * 0.8, y + h * 0.85);
    // Harf
    doc.setFontSize(Math.min(w, h) * 0.3);
    doc.text('A', cx, y + h * 0.65, { align: 'center' });
  }

  // Yüzey Kalitesi
  private drawSurfaceFinish(doc: jsPDF, x: number, y: number, w: number, h: number): void {
    const cx = x + w / 2;
    // V şekli
    doc.line(x + w * 0.1, y + h * 0.95, cx, y + h * 0.2);
    doc.line(cx, y + h * 0.2, x + w * 0.9, y + h * 0.95);
    // Alt çizgi
    doc.line(x + w * 0.3, y + h * 0.95, x + w * 0.7, y + h * 0.95);
    // Ra yazısı
    doc.setFontSize(Math.min(w, h) * 0.2);
    doc.text('Ra', cx, y + h * 0.55, { align: 'center' });
  }

  // Uyarı Üçgeni
  private drawWarningTriangle(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    const points = [
      { x: x + w / 2, y: y + h * 0.05 },
      { x: x + w * 0.95, y: y + h * 0.9 },
      { x: x + w * 0.05, y: y + h * 0.9 }
    ];
    this.drawPolygon(doc, points, drawMode, fillColor || '#f1c40f');
    // Ünlem
    doc.setFontSize(Math.min(w, h) * 0.5);
    doc.setTextColor('#000000');
    doc.text('!', x + w / 2, y + h * 0.75, { align: 'center' });
  }

  // Tehlike İşareti
  private drawDangerSign(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const r = Math.min(w, h) / 2 * 0.9;
    doc.setFillColor(fillColor || '#e74c3c');
    doc.circle(cx, cy, r, 'FD');
    doc.setFontSize(Math.min(w, h) * 0.55);
    doc.setTextColor('#ffffff');
    doc.text('✕', cx, cy + r * 0.2, { align: 'center' });
  }

  // Yasak İşareti
  private drawProhibition(doc: jsPDF, x: number, y: number, w: number, h: number): void {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const r = Math.min(w, h) / 2 * 0.9;
    doc.setDrawColor('#c0392b');
    doc.setLineWidth(r * 0.15);
    doc.circle(cx, cy, r, 'S');
    // Çapraz çizgi
    const offset = r * 0.7;
    doc.line(cx - offset, cy - offset, cx + offset, cy + offset);
  }

  // Bilgi Dairesi
  private drawInfoCircle(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const r = Math.min(w, h) / 2 * 0.9;
    doc.setFillColor(fillColor || '#3498db');
    doc.circle(cx, cy, r, 'F');
    doc.setFontSize(Math.min(w, h) * 0.55);
    doc.setTextColor('#ffffff');
    doc.text('i', cx, cy + r * 0.2, { align: 'center' });
  }

  // Zorunluluk İşareti
  private drawMandatory(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const r = Math.min(w, h) / 2 * 0.9;
    doc.setFillColor(fillColor || '#2980b9');
    doc.circle(cx, cy, r, 'F');
    doc.setFontSize(Math.min(w, h) * 0.5);
    doc.setTextColor('#ffffff');
    doc.text('!', cx, cy + r * 0.2, { align: 'center' });
  }

  // Kalite Damgası
  private drawQualityStamp(doc: jsPDF, x: number, y: number, w: number, h: number): void {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const r = Math.min(w, h) / 2 * 0.9;
    doc.setDrawColor('#27ae60');
    doc.setLineWidth(0.5);
    doc.circle(cx, cy, r, 'S');
    doc.circle(cx, cy, r * 0.78, 'S');
    doc.setFontSize(Math.min(w, h) * 0.15);
    doc.setTextColor('#27ae60');
    doc.text('KALİTE', cx, cy - r * 0.15, { align: 'center' });
    doc.setFontSize(Math.min(w, h) * 0.25);
    doc.text('OK', cx, cy + r * 0.25, { align: 'center' });
  }

  // Kontrol İşareti
  private drawInspectionMark(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    // Kare
    doc.setFillColor(fillColor || '#ffffff');
    doc.rect(x + w * 0.05, y + h * 0.05, w * 0.9, h * 0.9, drawMode);
    // Onay işareti
    doc.setDrawColor('#27ae60');
    doc.setLineWidth(Math.min(w, h) * 0.08);
    doc.line(x + w * 0.2, y + h * 0.5, x + w * 0.42, y + h * 0.72);
    doc.line(x + w * 0.42, y + h * 0.72, x + w * 0.8, y + h * 0.28);
  }

  // İşlem Kutusu
  private drawProcessBox(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    doc.setFillColor(fillColor || '#e8f4fd');
    doc.roundedRect(x + w * 0.05, y + h * 0.2, w * 0.9, h * 0.6, 1, 1, drawMode);
  }

  // Karar Elması
  private drawDecision(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    const points = [
      { x: x + w / 2, y: y + h * 0.05 },
      { x: x + w * 0.95, y: y + h / 2 },
      { x: x + w / 2, y: y + h * 0.95 },
      { x: x + w * 0.05, y: y + h / 2 }
    ];
    this.drawPolygon(doc, points, drawMode, fillColor || '#fef9e7');
  }

  // Bağlayıcı
  private drawConnector(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const r = Math.min(w, h) / 2 * 0.7;
    doc.setFillColor(fillColor || '#d5f5e3');
    doc.circle(cx, cy, r, drawMode);
  }

  // Döküman
  private drawDocument(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    doc.setFillColor(fillColor || '#ffffff');
    // Üst kısım düz
    doc.rect(x + w * 0.05, y + h * 0.1, w * 0.9, h * 0.7, drawMode);
    // Alt dalga (basitleştirilmiş)
    doc.line(x + w * 0.05, y + h * 0.8, x + w * 0.35, y + h * 0.9);
    doc.line(x + w * 0.35, y + h * 0.9, x + w * 0.65, y + h * 0.75);
    doc.line(x + w * 0.65, y + h * 0.75, x + w * 0.95, y + h * 0.85);
  }

  // Veritabanı
  private drawDatabase(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    const rx = w * 0.42;
    const ry = h * 0.15;
    const cx = x + w / 2;
    doc.setFillColor(fillColor || '#ebdef0');
    // Üst elips
    doc.ellipse(cx, y + ry, rx, ry, 'FD');
    // Gövde
    doc.rect(x + w * 0.08, y + ry, w * 0.84, h * 0.6, 'F');
    doc.line(x + w * 0.08, y + ry, x + w * 0.08, y + h - ry);
    doc.line(x + w * 0.92, y + ry, x + w * 0.92, y + h - ry);
    // Alt elips
    doc.ellipse(cx, y + h - ry, rx, ry, 'FD');
  }

  // Başlat/Bitir (Terminator)
  private drawTerminator(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    const rx = h * 0.25;
    doc.setFillColor(fillColor || '#fadbd8');
    doc.roundedRect(x + w * 0.05, y + h * 0.25, w * 0.9, h * 0.5, rx, rx, drawMode);
  }

  // Etiket
  private drawTag(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    const points = [
      { x: x + w * 0.15, y: y + h * 0.1 },
      { x: x + w * 0.85, y: y + h * 0.1 },
      { x: x + w * 0.95, y: y + h * 0.5 },
      { x: x + w * 0.85, y: y + h * 0.9 },
      { x: x + w * 0.15, y: y + h * 0.9 },
      { x: x + w * 0.15, y: y + h * 0.5 }
    ];
    this.drawPolygon(doc, points, drawMode, fillColor || '#fdebd0');
  }

  // Bayrak
  private drawFlag(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    // Direk
    doc.line(x + w * 0.15, y + h * 0.05, x + w * 0.15, y + h * 0.95);
    // Bayrak (basitleştirilmiş dikdörtgen)
    doc.setFillColor(fillColor || '#e74c3c');
    doc.rect(x + w * 0.15, y + h * 0.1, w * 0.7, h * 0.35, 'FD');
  }

  // Raptiye
  private drawPin(doc: jsPDF, x: number, y: number, w: number, h: number, drawMode: string, fillColor?: string): void {
    const cx = x + w / 2;
    const topR = Math.min(w, h) * 0.25;
    // Üst daire (baş)
    doc.setFillColor(fillColor || '#e74c3c');
    doc.circle(cx, y + topR, topR, 'FD');
    // İğne
    doc.setFillColor('#888888');
    const points = [
      { x: cx - topR * 0.4, y: y + topR * 1.7 },
      { x: cx + topR * 0.4, y: y + topR * 1.7 },
      { x: cx, y: y + h * 0.95 }
    ];
    this.drawPolygon(doc, points, 'FD', '#888888');
  }

  // ============ MEASUREMENTS ============

  private renderMeasurements(
    doc: jsPDF,
    element: PdfElement,
    x: number,
    y: number,
    context: ExportContext
  ): void {
    const measurements = context.measurements || [];

    if (measurements.length === 0) {
      doc.setFontSize(9);
      doc.setTextColor('#999999');
      doc.text('Ölçüm yok', x, y + 5);
      return;
    }

    // Tablo olarak çiz
    const lineHeight = 5;
    const labelWidth = element.width * 0.5;
    const valueWidth = element.width * 0.5;

    doc.setFontSize(8);
    doc.setDrawColor('#cccccc');

    // Başlık
    doc.setFillColor('#f0f0f0');
    doc.rect(x, y, element.width, lineHeight, 'FD');
    doc.setFont(this.getActiveFont(), 'bold');
    doc.setTextColor('#000000');
    doc.text('Ölçüm', x + 2, y + 3.5);
    doc.text('Değer', x + labelWidth + 2, y + 3.5);

    // Veriler
    doc.setFont(this.getActiveFont(), 'normal');
    let currentY = y + lineHeight;

    for (let i = 0; i < Math.min(measurements.length, 10); i++) {
      const m = measurements[i];
      doc.rect(x, currentY, element.width, lineHeight, 'S');

      const label = m.label || `${m.type} ${i + 1}`;
      const value = `${m.value.toFixed(2)} ${m.unit}`;

      doc.text(label, x + 2, currentY + 3.5, { maxWidth: labelWidth - 4 });
      doc.text(value, x + labelWidth + 2, currentY + 3.5);

      currentY += lineHeight;
    }

    // Daha fazla varsa belirt
    if (measurements.length > 10) {
      doc.setFontSize(7);
      doc.setTextColor('#666666');
      doc.text(`... ve ${measurements.length - 10} ölçüm daha`, x + 2, currentY + 3);
    }
  }

  // ============ QRCODE ============

  private async renderQRCode(
    doc: jsPDF,
    element: PdfElement,
    x: number,
    y: number,
    resolvedParameters: Map<string, string>
  ): Promise<void> {
    // QR içeriğinde parametre referanslarını çözümle
    let qrContent = element.properties.qrContent || '';
    qrContent = this.resolveText(qrContent, resolvedParameters);

    if (!qrContent) {
      // İçerik yoksa placeholder göster
      doc.setDrawColor('#cccccc');
      doc.setFillColor('#f5f5f5');
      doc.rect(x, y, element.width, element.height, 'FD');
      doc.setFontSize(8);
      doc.setTextColor('#999999');
      doc.text('QR', x + element.width / 2, y + element.height / 2, { align: 'center' });
      return;
    }

    try {
      // Dinamik import ile qrcode kütüphanesini yükle
      // @ts-ignore - qrcode runtime'da mevcut (ana projede yüklü)
      const QRCode = await import('qrcode');

      // QR kodu data URL olarak oluştur
      const qrSize = Math.min(element.width, element.height) * 4; // Yüksek çözünürlük için 4x
      const qrDataUrl = await QRCode.toDataURL(qrContent, {
        width: qrSize,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#ffffff'
        },
        errorCorrectionLevel: 'M'
      });

      // QR kodunu PDF'e ekle
      doc.addImage(qrDataUrl, 'PNG', x, y, element.width, element.height);
    } catch (error) {
      console.error('QR kod oluşturma hatası:', error);
      // Hata durumunda placeholder göster
      doc.setDrawColor('#ff0000');
      doc.setFillColor('#ffeeee');
      doc.rect(x, y, element.width, element.height, 'FD');
      doc.setFontSize(6);
      doc.setTextColor('#cc0000');
      doc.text('QR Hata', x + element.width / 2, y + element.height / 2, { align: 'center' });
    }
  }

  // ============ HELPERS ============

  private getPageFormat(settings: PageSettings): [number, number] {
    if (settings.size === 'Custom') {
      return [settings.width, settings.height];
    }

    const size = PAGE_SIZES[settings.size];
    if (settings.orientation === 'landscape') {
      return [size.height, size.width];
    }
    return [size.width, size.height];
  }
}
